import { useCallback } from 'react'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { rememberPaymentFlow } from 'utilities/analytics/paymentFlow'
import { addOnlinePayForOtherInLoan, postGatewayPaymentOneItemInstallment } from 'services/easyPay'
import useGatewayHandoff from 'utilities/hooks/useGatewayHandoff'

export default function useSetPay(data?: any) {
    const { locked, isLocked, lock, hold, release } = useGatewayHandoff()
    const { isLoading, mutate } = useMutation({
        mutationFn: (payload: any) => {
            return postGatewayPaymentOneItemInstallment(payload)
        },
        onSuccess: (result, variables) => {
            localStorage.setItem(
                'toBePaidTransactions',
                JSON.stringify({
                    // `type` is what the gateway callback reports as
                    // `flow_type` on payment_succeeded / payment_failed, so it
                    // has to be the same string this screen tags the redirect
                    // with. It used to say «pay-for-other», one character away
                    // from the unrelated «pay-for-others» flow the callback
                    // branches on, which split one funnel across two names.
                    //
                    // The callback has no branch for this value and falls
                    // through to /funds — which is the right landing for a
                    // repayment — so the rename is behaviour-preserving.
                    type: 'loan_installment',
                    data: data ? data : [],
                    //@ts-ignore
                    // pk: result.cashin.transaction.pk,
                    //@ts-ignore
                    // gateway: 0,
                })
            )
            const items: any[] = variables?.paymentItems ?? []
            posthog.capture('payment_gateway_redirect', { flow_type: 'loan_installment' })
            rememberPaymentFlow({
                flow_type: 'loan_installment',
                amount: items.reduce(
                    (sum, item) =>
                        sum + Number(item?.itemAmount ?? 0) +
                        Number(item?.commission ?? 0),
                    0
                ),
                gateway: variables?.gateway?.uniqueCode ?? null,
                box_id: items[0]?.box_id ?? null,
                box_type: 'mnp',
            })
            // The button stays down while the browser is on its way to the
            // bank — see `useGatewayHandoff`.
            hold()
            //@ts-ignore
            window.location.replace(result?.payment_link)
            toast.success('در حال انتقال به صفحه کارت به کارت')
        },
        onError: (error: any) => {
            release()
            // Surface the backend's specific message. An amount over the
            // کارت‌به‌کارت ceiling comes back as {"error": "سقف واریز ..."} — the
            // one thing the payer can act on — and «مشکلی پیش آمده» told
            // them nothing. Fall back to the generic toast when the body has no
            // string message (serializer errors are an object, network errors
            // have no body at all).
            const backendMessage = error?.response?.data?.error
            posthog.capture('payment_initiate_failed', {
                flow_type: 'loan_installment',
                error_message:
                    typeof backendMessage === 'string' ? backendMessage : null,
                status: error?.response?.status,
            })
            toast.error(
                typeof backendMessage === 'string' && backendMessage
                    ? backendMessage
                    : 'مشکلی پیش آمده!'
            )
        },
    })
    const pay = useCallback(
        (payload: any) => {
            // Refused by the ref, not by `isLoading`: a second tap in the same
            // tick lands before React has re-rendered `disabled`.
            if (isLocked()) return
            lock()
            mutate(payload)
        },
        [isLocked, lock, mutate]
    )
    return { mutate: pay, isLoading: isLoading || locked }
}
